import { useQuestionnaireWebsiteScanQuery } from '@dieterApi/scan/useQuestionnaireWebsiteScanQuery';
import { CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Always-visible floating chip that reflects the progress of the embedded
 * questionnaire website scan. Lets the user see — while answering questions —
 * whether the scan is still running, so they can decide to wait for a
 * question the scan would pre-fill. Polls in the background and self-hides
 * when there is no scan to show.
 */
export function WebsiteScanProgressChip() {
  const { t } = useTranslation();
  const { data } = useQuestionnaireWebsiteScanQuery();
  const [dismissed, setDismissed] = useState(false);

  const scan = data?.questionnaireWebsiteScan;

  // Nothing to show: no scan started, or the user dismissed the finished chip.
  if (!scan || scan.status === 'none' || !scan.propertyId) return null;
  if (scan.scanFinished && dismissed) return null;

  const failed = scan.status === 'failed';
  const running = !scan.scanFinished && !failed;

  const progress =
    scan.progressCurrent != null && scan.progressTotal
      ? Math.min(100, Math.round((scan.progressCurrent / scan.progressTotal) * 100))
      : null;

  return (
    <div className="fixed inset-x-3 bottom-24 z-40 sm:inset-x-auto sm:bottom-auto sm:right-4 sm:top-40 sm:max-w-[280px]">
      <div
        className={`flex items-center gap-2.5 rounded-lg border px-3.5 py-2.5 shadow-md backdrop-blur-sm ${
          failed ? 'border-amber-300 bg-amber-50/95' : 'border-primary/30 bg-white/95'
        }`}
      >
        {running ? (
          <CircularProgress
            size={18}
            variant={progress != null ? 'determinate' : 'indeterminate'}
            value={progress ?? undefined}
          />
        ) : failed ? (
          <span className="text-lg leading-none">⚠️</span>
        ) : (
          <CheckCircleIcon className="text-primary" fontSize="small" />
        )}

        <span className="text-sm font-medium text-gray-700">
          {running &&
            (progress != null
              ? t('components.block.quest.scan.progress.running_percent', { percent: progress })
              : `${t('components.block.quest.scan.progress.running')} …`)}
          {scan.scanFinished && t('components.block.quest.scan.progress.done')}
          {failed && t('components.block.quest.scan.progress.failed')}
        </span>

        {(scan.scanFinished || failed) && (
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="ml-1 text-gray-400 hover:text-gray-600"
            aria-label={t('components.block.quest.scan.progress.close')}
          >
            <CloseIcon fontSize="small" />
          </button>
        )}
      </div>
    </div>
  );
}
